/**
 * Supporting evidence chapter: section intro and types of medical records
 * pages. These determine which evidence flows (VA medical records, private
 * medical records) follow.
 */
import {
  checkboxGroupSchema,
  checkboxGroupUI,
  titleUI,
  yesNoSchema,
  yesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import React from 'react';
import { standardTitle } from '../../../form0781/content';
import { confirmEvidenceRemovalPage } from '../../content/confirmEvidenceRemovalPage';
import {
  anyEvidence,
  buildItemGroups,
  removalAlert,
} from '../../content/evidenceRemovalContent';
import {
  evidenceRequestNote,
  evidenceRequestQuestion,
} from '../../content/evidenceRequest';
import {
  MedicalRecordTypesDescription,
  medicalRecordTypesQuestion,
} from '../../content/medicalRecordTypes';
import { SupportingEvidenceOrientation } from '../../content/supportingEvidenceOrientation';
import { isBDD } from '../../utils/bdd';
import { separationHealthAssessmentPages } from './separationHealthAssessmentPages';
import { serviceTreatmentRecordsPages } from './serviceTreatmentRecordsPages';
import { wantsEvidenceRequest } from './utils';

/**
 * Destructive modal for /supporting-evidence/medical-records
 * Displays when user changes their answer from Yes to No and clicks Continue,
 * and they have already uploaded medical records
 */
const getMedicalRecordsEvidence = formData => ({
  vaFacilities: formData?.vaTreatmentFacilities || [],
  providerFacilities: formData?.providerFacility || [],
  pmrUploads: formData?.privateMedicalRecordsUploads || [],
});

const EvidenceRequestRemovalPage = confirmEvidenceRemovalPage({
  shouldConfirm: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getMedicalRecordsEvidence(formData);
    return (
      formData?.['view:hasMedicalRecords'] === false &&
      anyEvidence(vaFacilities, providerFacilities, pmrUploads)
    );
  },
  getItemGroups: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getMedicalRecordsEvidence(formData);
    return buildItemGroups([
      {
        items: vaFacilities,
        kind: 'vaFacility',
        phrase:
          'remove the information you shared about these VA or military healthcare providers',
      },
      {
        items: providerFacilities,
        kind: 'provider',
        phrase:
          'remove the information you shared about these other healthcare providers',
      },
      {
        items: pmrUploads,
        kind: 'record',
        phrase: 'delete these medical records you uploaded',
      },
    ]);
  },
  removeItems: formData => {
    const updated = { ...formData };
    delete updated.vaTreatmentFacilities;
    delete updated.providerFacility;
    delete updated.privateMedicalRecordsUploads;
    delete updated['view:medicalRecordTypes'];
    delete updated['view:privateMedicalRecordsAccess'];
    return updated;
  },
  revertSelection: formData => ({
    ...formData,
    'view:hasMedicalRecords': true,
  }),
  modalTitle: 'Change your medical records?',
  modalIntro: (
    <p>You can choose not to submit medical records to support your claim.</p>
  ),
  primaryButtonText: 'Change and remove',
  secondaryButtonText: 'Cancel change',
  // The user may have listed VA facilities, other healthcare providers, or
  // uploaded records, or any mix. The alert names whichever applies.
  // "Other healthcare providers" is only used when the VA ones aren't also
  // being removed — otherwise the plain term covers both.
  alertText: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getMedicalRecordsEvidence(formData);
    const actions = [];
    if (vaFacilities.length) {
      actions.push('removed information about your healthcare providers');
    } else if (providerFacilities.length) {
      actions.push('removed information about your other healthcare providers');
    }
    if (pmrUploads.length) {
      actions.push('deleted the medical records you uploaded');
    }
    return removalAlert(actions);
  },
});

/**
 * Destructive modal for /supporting-evidence/medical-records-types
 * Displays when the user unchecks a record type they've already given us
 * information for, and clicks Continue. Unlike the evidence-request modal,
 * this removes only the unchecked type's evidence — the other type stays.
 */
const getUncheckedEvidence = formData => {
  const types = formData?.['view:medicalRecordTypes'] || {};
  const { vaFacilities, providerFacilities, pmrUploads } =
    getMedicalRecordsEvidence(formData);
  return {
    // Only evidence belonging to a now-unchecked type is at risk.
    vaFacilities: types.vaOrMilitary ? [] : vaFacilities,
    providerFacilities: types.privateProvider ? [] : providerFacilities,
    pmrUploads: types.privateProvider ? [] : pmrUploads,
  };
};

const MedicalRecordTypesRemovalPage = confirmEvidenceRemovalPage({
  shouldConfirm: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getUncheckedEvidence(formData);
    return anyEvidence(vaFacilities, providerFacilities, pmrUploads);
  },
  getItemGroups: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getUncheckedEvidence(formData);
    return buildItemGroups([
      {
        items: vaFacilities,
        kind: 'vaFacility',
        phrase:
          'remove information you entered about these VA or military healthcare providers',
      },
      {
        items: providerFacilities,
        kind: 'provider',
        phrase:
          'remove information you shared about these other healthcare providers',
      },
      {
        items: pmrUploads,
        kind: 'record',
        phrase: 'delete these medical records you uploaded',
      },
    ]);
  },
  removeItems: formData => {
    const types = formData?.['view:medicalRecordTypes'] || {};
    const updated = { ...formData };
    if (!types.vaOrMilitary) {
      delete updated.vaTreatmentFacilities;
    }
    if (!types.privateProvider) {
      delete updated.providerFacility;
      delete updated.privateMedicalRecordsUploads;
      delete updated['view:privateMedicalRecordsAccess'];
    }
    return updated;
  },
  // Dismissing re-checks whichever types still have evidence, putting the
  // page back the way the user left it.
  revertSelection: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getUncheckedEvidence(formData);
    return {
      ...formData,
      'view:medicalRecordTypes': {
        ...formData?.['view:medicalRecordTypes'],
        ...(vaFacilities.length ? { vaOrMilitary: true } : {}),
        ...(providerFacilities.length || pmrUploads.length
          ? { privateProvider: true }
          : {}),
      },
    };
  },
  modalTitle: 'Change type of medical records?',
  modalIntro: (
    <p>You can change the type of medical records you’d like us to access.</p>
  ),
  // Uploaded files are deleted; everything else is information we remove.
  primaryButtonText: formData => {
    const { vaFacilities, providerFacilities } = getUncheckedEvidence(formData);
    return vaFacilities.length || providerFacilities.length
      ? 'Change and remove'
      : 'Change and delete';
  },
  secondaryButtonText: 'Cancel change',
  alertText: formData => {
    const { vaFacilities, providerFacilities, pmrUploads } =
      getUncheckedEvidence(formData);
    const actions = [];
    if (vaFacilities.length) {
      actions.push(
        'removed information about your VA or military healthcare providers',
      );
    }
    if (providerFacilities.length) {
      actions.push('removed information about your other healthcare providers');
    }
    if (pmrUploads.length) {
      actions.push('deleted the medical records you uploaded');
    }
    return removalAlert(actions);
  },
});

export const sectionIntroPages = {
  // Always displayed. Matches the all-claims (v1) orientation page. The URL
  // moved from /supporting-evidence/orientation per the Step 4 URL spec in
  // Figma (#157626).
  orientation: {
    path: 'supporting-evidence/intro',
    title: '',
    uiSchema: {
      'ui:title': standardTitle(
        'Supporting evidence for your disability claim',
      ),
      'ui:description': SupportingEvidenceOrientation,
    },
    schema: { type: 'object', properties: {} },
  },
  // Separation Health Assessment (BDD only). Asked and uploaded before the
  // medical records question.
  ...separationHealthAssessmentPages,
  // Service treatment records (BDD only): entry question, then a List & Loop
  // upload (one file per item) and its review page.
  ...serviceTreatmentRecordsPages,
  // Always displayed. Decision point: gates /medical-records-types (and
  // everything behind it). Simplified v2 version of the v1 evidence request
  // page — same question and field, but no legacy IPF derivation or removal
  // modal, and markup per the finalized 2026 design (step4_flow0.png, #151946).
  evidenceRequest: {
    path: 'supporting-evidence/medical-records',
    title: 'Medical records that support your disability claim',
    // BDD claims use the /other-medical-records-bdd page instead (see
    // bddEvidencePages), so this standard question is skipped for them.
    depends: formData => !isBDD(formData),
    // Intercepts Continue with a destructive confirmation modal when "No" is
    // selected and medical-records evidence already exists (v1 precedent:
    // all-claims EvidenceRequestPage).
    CustomPage: EvidenceRequestRemovalPage,
    CustomPageReview: null,
    uiSchema: {
      'ui:title': standardTitle(
        'Medical records that support your disability claim',
      ),
      'view:hasMedicalRecords': yesNoUI({
        title: evidenceRequestQuestion.label,
        labels: evidenceRequestQuestion.labels,
        descriptions: evidenceRequestQuestion.descriptions,
        tile: true,
      }),
      'view:evidenceRequestNote': {
        'ui:description': evidenceRequestNote,
      },
    },
    schema: {
      type: 'object',
      required: ['view:hasMedicalRecords'],
      properties: {
        'view:hasMedicalRecords': yesNoSchema,
        'view:evidenceRequestNote': { type: 'object', properties: {} },
      },
    },
  },
  // Shown only when "Yes" is selected on the /medical-records page. The
  // selected record types gate the VA and private medical records flows.
  medicalRecords: {
    path: 'supporting-evidence/medical-records-types',
    title: 'Types of medical records',
    // Skipped for BDD claims (the /other-medical-records-bdd page sets the
    // private-records field directly).
    depends: formData => !isBDD(formData) && wantsEvidenceRequest(formData),
    // Intercepts Continue with a destructive confirmation modal when a
    // record type is unchecked after evidence was already provided for it
    // (v1 precedent: all-claims MedicalRecordsPage).
    CustomPage: MedicalRecordTypesRemovalPage,
    CustomPageReview: null,
    uiSchema: {
      ...titleUI('Types of medical records'),
      'view:medicalRecordTypes': checkboxGroupUI({
        title: medicalRecordTypesQuestion.title,
        required: true,
        labels: medicalRecordTypesQuestion.labels,
        errorMessages: {
          required: medicalRecordTypesQuestion.requiredError,
        },
      }),
      'view:medicalRecordTypesInfo': {
        'ui:description': MedicalRecordTypesDescription,
      },
    },
    schema: {
      type: 'object',
      required: ['view:medicalRecordTypes'],
      properties: {
        'view:medicalRecordTypes': checkboxGroupSchema([
          'vaOrMilitary',
          'privateProvider',
        ]),
        'view:medicalRecordTypesInfo': { type: 'object', properties: {} },
      },
    },
  },
};

export default sectionIntroPages;
